<!-- eslint-disable max-len -->
<template>
  <div class="view-contents">
    <b-overlay :show="isLoading">
    <b-modal id="serverOrLocal" title="First Site Visit" size="lg">
      <p>
        There is a newer version of this First Site Visit currently stored in Acorn.  Would you like to update to the newer version or keep the version you have downloaded to your browser?
      </p>
      <p>
        If you take the new version, you may lose changes you have made.
      </p>
      <template v-slot:modal-footer>
        <b-button
          size="sm"
          variant="danger"
          @click="$bvModal.hide('serverOrLocal')">
          Keep This Site Visit
        </b-button>
        <b-button
          size="sm"
          variant="success"
          @click="copyServerSiteVisit">
          Get Latest
        </b-button>
      </template>
    </b-modal>
      <b-card no-body>
        <b-tabs
          card
          align="center"
          @activate-tab="activateTab"
          v-model=tabIndex
        >
          <b-tab v-for="(section, index) in schema"
                 :key="section.legend"
                 :title="section.legend"
                 :after-change="afterChange"
                 class="tabbed-form-header"
          >
            <b-card-body>
              <b-alert
                :show="successDismissCountDown"
                dismissible
                variant="success"
                @dismissed="successDismissCountDown=0"
                @dismiss-count-down="successCountDownChanged"
              >
                First Site Visit has been saved
              </b-alert>
              <b-alert
                :show="warningDismissCountDown"
                dismissible
                variant="warning"
                @dismissed="warningDismissCountDown=0"
                @dismiss-count-down="warningCountDownChanged"
              >
                You are offline, First Site Visit is only saved on your machine.  Please submit again when you are back online.
              </b-alert>
              <b-alert :show="showTaskCreatedSuccess" dismissible variant="info">
                <p>A new follow-up task has been automatically generated in Insightly in a new browser tab.</p>
                <p>Please make any needed updates to the Task.</p>
              </b-alert>
              <b-alert :show="showTaskCreatedError" dismissible variant="danger">
                <p>A new follow-up task could not be automatically generated in Insightly in a new browser tab.</p>
                <p>Please Contact your system administrator.</p>
              </b-alert>
              <div class="row section-header">
                <div class="col-10 bold-title">
                  {{ section.headerName }}
                  <b-button
                    pill
                    v-if="index === schema.length -1"
                    variant="primary"
                    size="sm"
                    class="submit-form-button"
                    :disabled="isSubmitting || !activeTabValid || !requiredNextStepsFieldsValidated()"
                    @click="submitSiteVisit">
                    Submit
                  </b-button>
                  <b-button
                    pill
                    variant="primary"
                    size="sm"
                    class="submit-form-button"
                    :disabled="isSaving || !activeTabValid"
                    @click="saveSiteVisit">
                    Save
                  </b-button>
                </div>
              </div>
              <b-alert
                :show="!activeTabValid && showErrorsOnActiveTab"
                dismissible
                variant="danger"
              >
                Please fix errors on this tab before moving to another tab
              </b-alert>
              <invasives
                v-if="section.legend === 'Invasives'"
                :schema="section"
                :model="siteVisitModel"
                ref="invasive-tab"
                @validated="onValidated"
              />
              <template v-else>
                <VueFormGenerator
                  v-if="dataLoaded"
                  :schema="section"
                  :model="siteVisitModel"
                  :options="formOptions"
                  :ref="section.legend"
                  @validated="onValidated"
                  :key="formKey"
                />
              </template>
            </b-card-body>
          </b-tab>
        </b-tabs>
      </b-card>
    </b-overlay>
  </div>
</template>
<script>
/* eslint-disable max-len, no-console, padded-blocks */
import api from '@/api';
import { mapState } from 'vuex';
import Invasives from '@/components/common/Invasives.vue';
import AlertManager from '../mixins/AlertManager';
import FormSchemaHelper from '../mixins/formSchemaHelper';
import showSidePanelToggle from '../mixins/showSidePanelToggle';

export default {
  name: 'SiteVisit',
  mixins: [FormSchemaHelper, AlertManager, showSidePanelToggle],
  data() {
    return {
      schema: this.getFirstSiteVisitSchema().groups,
      formOptions: {
        validateAfterLoad: true,
        validateAfterChanged: true,
      },
      siteVisitModel: {},
      isLoading: true,
      isSubmitting: false,
      isSaving: false,
      activeTabValid: true,
      showTaskCreatedSuccess: false,
      showTaskCreatedError: false,
      tabIndex: 0,
      dataLoaded: false,
      formKey: 0,
    };
  },
  components: {
    Invasives,
  },
  computed: {
    ...mapState(['currentSite', 'config']),
    ...mapState('SiteVisit', {
      siteVisit: (state) => state.siteVisit,
      serverSiteVisit: (state) => state.serverSiteVisit,
      showErrorsOnActiveTab: (state) => state.showErrorsOnActiveTab,
    }),
  },
  async mounted() {
    await this.fetchData(parseInt(this.$route.params.id, 10)).then(() => {
      this.dataLoaded = true;
      if (this.serverSiteVisit) {
        this.$bvModal.show('serverOrLocal');
      }
    });
  },
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch('SiteVisit/setSiteVisit', this.siteVisitModel).then(() => {
      this.$store.dispatch('SiteVisit/persistCurrentSiteVisit', this.siteVisitModel);
    });
    next();
  },
  methods: {
    cloneObject(sourceObj) {
      const newObj = {};
      Object.assign(newObj, sourceObj);
      return newObj;
    },
    async fetchSiteVisit(id) {
      await this.$store.dispatch('fetchSiteAsync', id).then(async () => {
        await this.$store.dispatch('SiteVisit/fetchSiteVisit',
          {
            currentSite: this.currentSite,
            currentUser: this.$store.getters['User/getCurrentUser'].name,
            initializeVisit: true,
          }).then(() => {
          //  make a copy so we can manipulate it in the forms
          this.siteVisitModel = this.cloneObject(this.siteVisit);
          this.isLoading = false;
          this.$store.dispatch('SiteVisit/persistCurrentSiteVisit');
          if (this.$route.query.tab) {
            this.schema.forEach((item, index) => {
              if (item.legend.toLowerCase().replace(' ', '') === this.$route.query.tab) {
                this.tabIndex = index;
              }
            });
          }
        });
      });
    },
    async fetchData(id) {
      // only fetch Site if we don't already have it
      if (this.currentSite && this.currentSite.Id === id) {
        await this.fetchSiteVisit(id);
      } else {
        await this.$store.dispatch('fetchSiteAsync', id).then(async () => {
          await this.fetchSiteVisit(id);
        });
      }
    },
    afterChange() {
      window.scrollTo(0, 0);
      return true;
    },
    // eslint-disable-next-line no-unused-vars
    activateTab(newTabIndex, prevTabIndex, bvEvent) {
      if (this.activeTabValid) {
        this.saveSiteVisit();
        window.scrollTo(0, 0);
      } else {
        this.$store.dispatch('SiteVisit/setShowErrorsOnActiveTab', true);
        window.scrollTo(0, 0);
        bvEvent.preventDefault();
      }
    },
    requiredNextStepsFieldsValidated() {
      if (this.siteVisitModel.technicalDetails
        && this.siteVisitModel.technicalDetails.furtherAssistance
        && this.siteVisitModel.technicalDetails.furtherAssistance.length > 0
        && this.siteVisitModel.technicalDetails.dateOfVisit) {
        return true;
      }
      return false;
    },
    // eslint-disable-next-line no-unused-vars
    onValidated(isValid, errors) {
      this.activeTabValid = isValid;
    },
    saveSiteVisit() {
      // dispatch an update to vuex when the state of the wizard changes
      this.isSubmitting = true;

      this.$store.dispatch('SiteVisit/setSiteVisit', this.siteVisitModel).then(async () => {

        this.siteVisitModel = this.cloneObject(this.siteVisit);
        await this.$store.dispatch('SiteVisit/persistCurrentSiteVisit', this.siteVisitModel);
        this.showSuccessAlert();
        this.isSubmitting = false;
      });
    },
    submitSiteVisit() {
      this.siteVisitModel.finished = true;

      if (navigator.onLine) {

        this.isSubmitting = true;

        this.$store.dispatch('SiteVisit/postSiteVisit', this.siteVisitModel).then(() => {
          this.siteVisitModel = this.cloneObject(this.siteVisit);
          this.$store.dispatch('SiteVisit/setSubmitSuccess', true).then(() => {
            api.post(`task/site/${this.siteVisitModel.siteId}`, this.siteVisitModel).then((response) => {
              if (response.status === 200) {
                this.showTaskCreatedSuccess = true;
                setTimeout(
                  () => {
                    this.$router.push({
                      path: `/site/${this.currentSite.Id}`,
                    });
                    window.open(response.data.uri, '_blank');
                  },
                  5000,
                );
              }
            }).catch((error) => {
              console.error(`Enter task/site/${this.siteVisitModel.siteId} catch()`);
              console.error(error);
              this.showTaskCreatedError = true;
              this.serverError = error;
              console.error(`Exit task/site/${this.siteVisitModel.siteId} catch()`);
            });
          });
        });
      } else {
        this.siteVisitModel.needsOnlineSubmit = true;
        this.siteVisitModel.localCompletedAt = new Date();
        this.$store.dispatch('SiteVisit/setSiteVisit', this.siteVisitModel).then(() => {
          this.$store.dispatch('SiteVisit/persistCurrentSiteVisit', this.siteVisitModel);
        });
        this.showWarningAlert();
      }
    },
    copyServerSiteVisit() {
      this.$store.dispatch('SiteVisit/copyServerSiteVisit');
      //  make a copy so we can manipulate it in the forms
      this.siteVisitModel = this.cloneObject(this.siteVisit);
      // change the key so the form re-renders
      this.formKey += 1;
      this.$bvModal.hide('serverOrLocal');
    },
  },
};
</script>
